'use client';

import { Fragment, ReactNode } from 'react';
import { Popover as HeadlessPopover, Transition } from '@headlessui/react';
import {
  Placement,
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react';

import { cn } from '@/utils/uiUtils';

interface PopoverProps {
  children: ReactNode;
  content: ReactNode;
  placement?: Placement;
  className?: string;
}

export function Popover({
  children,
  content,
  placement = 'bottom-start',
  className,
}: PopoverProps) {
  const { refs, floatingStyles } = useFloating({
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift()],
  });

  return (
    <HeadlessPopover className={cn('relative', className)}>
      {({ open }) => (
        <>
          <HeadlessPopover.Button ref={refs.setReference} as={Fragment}>
            {children}
          </HeadlessPopover.Button>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className='z-[9999]'
          >
            <Transition
              as={Fragment}
              show={open}
              enter='transition ease-out duration-200'
              enterFrom='opacity-0 translate-y-1'
              enterTo='opacity-100 translate-y-0'
              leave='transition ease-in duration-150'
              leaveFrom='opacity-100 translate-y-0'
              leaveTo='opacity-0 translate-y-1'
            >
              <div
                style={{ minWidth: '32rem', maxWidth: '36rem' }}
                className='rounded-lg border border-stone-300 bg-white p-4 text-sm text-gray-900 shadow-lg dark:border-stone-700 dark:bg-stone-700 dark:text-gray-100'
              >
                {content}
              </div>
            </Transition>
          </div>
        </>
      )}
    </HeadlessPopover>
  );
}
